/**
 * Expectation Evaluator — Programmatic rule engine for data request field validation.
 *
 * Supports rich comparison operators across all field types:
 *   - yesno:    eq, neq
 *   - number:   eq, neq, gt, gte, lt, lte, between
 *   - text:     eq, neq, contains, not_contains, starts_with, ends_with, matches (regex)
 *   - select:   eq, neq, in, not_in
 *   - date:     eq, neq, gt, gte, lt, lte, between
 *   - document: exists, not_exists
 *
 * Fields without an expectedResponse are considered to have no expectation (always pass).
 * For backward compatibility, if expectedResponse is a plain string, it's treated as { operator: 'eq', value: string }.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type V2ComparisonOperator =
	| 'eq' // equals (all types)
	| 'neq' // not equals (all types)
	| 'gt' // greater than (number, date)
	| 'gte' // greater than or equal (number, date)
	| 'lt' // less than (number, date)
	| 'lte' // less than or equal (number, date)
	| 'between' // inclusive range (number, date) — requires value + secondaryValue
	| 'contains' // string contains substring (text)
	| 'not_contains' // string does not contain (text)
	| 'starts_with' // string starts with (text)
	| 'ends_with' // string ends with (text)
	| 'in' // value in list (select, text, yesno)
	| 'not_in' // value not in list (select, text, yesno)
	| 'matches' // regex pattern match (text)
	| 'exists' // value is present / non-empty (document, any)
	| 'not_exists'; // value is absent / empty (any)

export interface V2ExpectationRule {
	/** The comparison operator */
	operator: V2ComparisonOperator;
	/** The value to compare against. For 'in'/'not_in', use an array. */
	value?: string | number | boolean | (string | number)[] | null;
	/** Upper bound for 'between' operator (inclusive) */
	secondaryValue?: string | number | null;
	/** Custom message to show when this rule fails (overrides default exception label) */
	message?: string;
}

/** What the evaluator returns for each rule evaluation */
export interface EvaluationResult {
	/** True if ALL expectation rules are met */
	met: boolean;
	/** The first rule that failed, if any */
	failedRule?: V2ExpectationRule;
	/** Human-readable description of why the expectation was not met */
	failureReason?: string;
}

// ---------------------------------------------------------------------------
// Normalization — backward compatibility with plain string expectedResponse
// ---------------------------------------------------------------------------

/**
 * Normalize an expectedResponse value into an array of rules.
 * - undefined/null → empty array (no expectations)
 * - plain string → [{ operator: 'eq', value: string }] (legacy format)
 * - single rule object → [rule]
 * - array of rules → rules as-is
 */
export function normalizeExpectation(
	raw: string | V2ExpectationRule | V2ExpectationRule[] | undefined | null
): V2ExpectationRule[] {
	if (raw == null) return [];
	if (typeof raw === 'string') {
		return [{ operator: 'eq', value: raw }];
	}
	if (Array.isArray(raw)) return raw;
	return [raw];
}

// ---------------------------------------------------------------------------
// Core evaluator
// ---------------------------------------------------------------------------

/**
 * Evaluate a single expectation rule against an answer value.
 *
 * @param rule     The expectation rule
 * @param answer   The raw string answer from the field (null/undefined = not answered)
 * @param fieldType The field type for context-aware parsing
 * @returns true if the expectation is MET (no exception needed)
 */
function evaluateRule(
	rule: V2ExpectationRule,
	answer: string | null | undefined,
	fieldType: string
): boolean {
	const op = rule.operator;

	// --- exists / not_exists don't need value comparison ---
	if (op === 'exists') {
		return answer != null && answer.trim() !== '';
	}
	if (op === 'not_exists') {
		return answer == null || answer.trim() === '';
	}

	// For all other operators, a missing answer means the expectation is not met
	if (answer == null || answer.trim() === '') return false;

	const ruleValue = rule.value;

	// --- in / not_in: check list membership ---
	if (op === 'in' || op === 'not_in') {
		const list = Array.isArray(ruleValue) ? ruleValue.map(String) : [];
		const isIn = list.some((v) => v.toLowerCase() === answer.toLowerCase());
		return op === 'in' ? isIn : !isIn;
	}

	// --- Numeric comparisons (number and date fields) ---
	if (fieldType === 'number' || fieldType === 'date') {
		return evaluateNumericOrDate(op, answer, ruleValue, rule.secondaryValue, fieldType);
	}

	// --- String operations (text, select, yesno, etc.) ---
	return evaluateString(op, answer, ruleValue);
}

/**
 * Evaluate numeric/date comparisons.
 * Numbers are compared numerically; dates are compared as ISO strings (lexicographic works for ISO 8601).
 */
function evaluateNumericOrDate(
	op: V2ComparisonOperator,
	answer: string,
	ruleValue: V2ExpectationRule['value'],
	secondaryValue: V2ExpectationRule['secondaryValue'],
	fieldType: string
): boolean {
	if (fieldType === 'number') {
		const numAnswer = parseFloat(answer);
		if (isNaN(numAnswer)) return false;
		const numExpected =
			typeof ruleValue === 'number' ? ruleValue : parseFloat(String(ruleValue ?? ''));
		if (isNaN(numExpected) && op !== 'between') return false;

		switch (op) {
			case 'eq':
				return numAnswer === numExpected;
			case 'neq':
				return numAnswer !== numExpected;
			case 'gt':
				return numAnswer > numExpected;
			case 'gte':
				return numAnswer >= numExpected;
			case 'lt':
				return numAnswer < numExpected;
			case 'lte':
				return numAnswer <= numExpected;
			case 'between': {
				const lo = typeof ruleValue === 'number' ? ruleValue : parseFloat(String(ruleValue ?? ''));
				const hi =
					typeof secondaryValue === 'number'
						? secondaryValue
						: parseFloat(String(secondaryValue ?? ''));
				if (isNaN(lo) || isNaN(hi)) return false;
				return numAnswer >= lo && numAnswer <= hi;
			}
			default:
				// Fall through to string comparison for text-like ops on numbers
				return evaluateString(op, answer, ruleValue);
		}
	}

	// Date comparisons — ISO 8601 strings sort lexicographically
	if (fieldType === 'date') {
		const dateAnswer = answer.trim();
		const dateExpected = String(ruleValue ?? '').trim();
		if (!dateExpected && op !== 'between') return false;

		switch (op) {
			case 'eq':
				return dateAnswer === dateExpected;
			case 'neq':
				return dateAnswer !== dateExpected;
			case 'gt':
				return dateAnswer > dateExpected;
			case 'gte':
				return dateAnswer >= dateExpected;
			case 'lt':
				return dateAnswer < dateExpected;
			case 'lte':
				return dateAnswer <= dateExpected;
			case 'between': {
				const lo = String(ruleValue ?? '').trim();
				const hi = String(secondaryValue ?? '').trim();
				if (!lo || !hi) return false;
				return dateAnswer >= lo && dateAnswer <= hi;
			}
			default:
				return evaluateString(op, dateAnswer, ruleValue);
		}
	}

	return false;
}

/**
 * Evaluate string-based comparison operators.
 */
function evaluateString(
	op: V2ComparisonOperator,
	answer: string,
	ruleValue: V2ExpectationRule['value']
): boolean {
	const expected = String(ruleValue ?? '');
	const answerLower = answer.toLowerCase();
	const expectedLower = expected.toLowerCase();

	switch (op) {
		case 'eq':
			return answerLower === expectedLower;
		case 'neq':
			return answerLower !== expectedLower;
		case 'contains':
			return answerLower.includes(expectedLower);
		case 'not_contains':
			return !answerLower.includes(expectedLower);
		case 'starts_with':
			return answerLower.startsWith(expectedLower);
		case 'ends_with':
			return answerLower.endsWith(expectedLower);
		case 'matches': {
			try {
				const re = new RegExp(expected, 'i');
				return re.test(answer);
			} catch {
				// Invalid regex pattern — treat as not matching
				return false;
			}
		}
		default:
			return answerLower === expectedLower;
	}
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Evaluate all expectation rules for a field. Returns whether the answer is acceptable.
 *
 * @param expectedResponse  The raw expectedResponse from the field config (string, rule, rules, or undefined)
 * @param answer            The user's answer (string or null)
 * @param fieldType         The V2DataRequestFieldType of the field
 * @returns EvaluationResult with met=true if all rules pass, otherwise the first failed rule + reason
 */
export function evaluateExpectation(
	expectedResponse: string | V2ExpectationRule | V2ExpectationRule[] | undefined | null,
	answer: string | null | undefined,
	fieldType: string
): EvaluationResult {
	const rules = normalizeExpectation(expectedResponse);

	// No expectations configured → always met
	if (rules.length === 0) {
		return { met: true };
	}

	for (const rule of rules) {
		if (!evaluateRule(rule, answer, fieldType)) {
			return {
				met: false,
				failedRule: rule,
				failureReason: rule.message || describeFailure(rule, answer, fieldType),
			};
		}
	}

	return { met: true };
}

/**
 * Quick check: is the answer "unexpected" (fails any expectation rule)?
 *
 * For backward compatibility with yesno fields that have no expectedResponse configured,
 * pass defaultExpected to auto-create a fallback rule.
 */
export function isAnswerUnexpected(
	expectedResponse: string | V2ExpectationRule | V2ExpectationRule[] | undefined | null,
	answer: string | null | undefined,
	fieldType: string,
	defaultExpected?: string
): boolean {
	// Apply default only for yesno fields with no explicit expectation
	const effective =
		expectedResponse == null && defaultExpected != null ? defaultExpected : expectedResponse;

	const result = evaluateExpectation(effective, answer, fieldType);
	return !result.met;
}

/**
 * Describe the expectation in human-readable terms (for UI labels / tooltips).
 */
export function describeExpectation(
	expectedResponse: string | V2ExpectationRule | V2ExpectationRule[] | undefined | null
): string {
	const rules = normalizeExpectation(expectedResponse);
	if (rules.length === 0) return '';
	return rules.map(describeRule).join(' AND ');
}

// ---------------------------------------------------------------------------
// Describe helpers — human-readable rule descriptions
// ---------------------------------------------------------------------------

const OP_LABELS: Record<V2ComparisonOperator, string> = {
	eq: 'equals',
	neq: 'does not equal',
	gt: 'is greater than',
	gte: 'is at least',
	lt: 'is less than',
	lte: 'is at most',
	between: 'is between',
	contains: 'contains',
	not_contains: 'does not contain',
	starts_with: 'starts with',
	ends_with: 'ends with',
	in: 'is one of',
	not_in: 'is not one of',
	matches: 'matches pattern',
	exists: 'is provided',
	not_exists: 'is not provided',
};

function describeRule(rule: V2ExpectationRule): string {
	const label = OP_LABELS[rule.operator] || rule.operator;

	if (rule.operator === 'exists' || rule.operator === 'not_exists') {
		return `Value ${label}`;
	}
	if (rule.operator === 'between') {
		return `Value ${label} ${rule.value} and ${rule.secondaryValue}`;
	}
	if (rule.operator === 'in' || rule.operator === 'not_in') {
		const list = Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value ?? '');
		return `Value ${label} [${list}]`;
	}
	return `Value ${label} "${rule.value}"`;
}

function describeFailure(
	rule: V2ExpectationRule,
	answer: string | null | undefined,
	_fieldType: string
): string {
	const got = answer ?? '(empty)';
	const expected = describeRule(rule);
	return `Expected: ${expected}. Got: "${got}"`;
}
